// API Client for Drive360 Admin Interface
const API_BASE_URL = 'http://eframeehs.in:3001';

// Simple test function to check connection
export const testConnection = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/health-check`);
    return response.ok;
  } catch (error) {
    console.error('Connection test failed:', error);
    return false;
  }
};

// Get locations for dropdown
export const getLocations = async (adminId = null) => {
  try {
    let url = `${API_BASE_URL}/api/locations`;
    if (adminId) {
      url += `?adminId=${adminId}`;
    }
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch locations');
    const locations = await response.json();
    console.log('Fetched locations:', locations);
    return locations;
  } catch (error) {
    console.error('Error fetching locations:', error);
    return [];
  }
};

// Get vehicle/equipment types for dropdown by location ID
export const getVehicleTypes = async (locationId = null) => {
  try {
    console.log('[API] Fetching vehicle types for locationId:', locationId);
    
    // Build URL with locationId query parameter if provided
    let url = `${API_BASE_URL}/api/vehicle-types`;
    if (locationId) {
      // Ensure locationId is passed as expected by the API
      url += `?locationId=${locationId}`;
    }
    
    console.log('[API] Request URL:', url);
    
    const response = await fetch(url);
    console.log('[API] Response status:', response.status);
    
    if (!response.ok) throw new Error('Failed to fetch vehicle types');
    
    const data = await response.json();
    console.log('[API] Raw vehicle data received:', data);
    
    if (!data || !Array.isArray(data)) {
      console.warn('[API] Received invalid data:', data);
      return [];
    }
    
    // Normalize the data for consistent property access across components
    const normalizedData = data.map(vehicle => {
      if (!vehicle) return null;
      
      return {
        VehicleID: vehicle.VehicleID,
        ScreenName: vehicle.ScreenName || vehicle.VehicleName,
        VehicleName: vehicle.VehicleName || '',
        LocationID: vehicle.LocationID
      };
    }).filter(v => v && v.VehicleID);
    
    return normalizedData;
  } catch (error) {
    console.error('[API] Error fetching vehicle types:', error);
    return [];
  }
};

// Get user assessment reports with optional filters
export const getUserWiseReport = async (filters = {}) => {
  try {
    // Build query parameters from filters
    const queryParams = new URLSearchParams();
    
    if (filters.fromDate) queryParams.append('fromDate', filters.fromDate);
    if (filters.toDate) queryParams.append('toDate', filters.toDate);
    if (filters.locationId) queryParams.append('locationId', filters.locationId);
    if (filters.vehicleId) queryParams.append('vehicleId', filters.vehicleId);
    
    const url = `${API_BASE_URL}/api/user-wise-report?${queryParams.toString()}`;
    const response = await fetch(url);
    
    if (!response.ok) throw new Error('Failed to fetch user report data');
    return await response.json();
  } catch (error) {
    console.error('Error fetching user report data:', error);
    return [];
  }
};

// Create a new user
export const createUser = async (userData) => {
  try {
    console.log('Making API request to create user...');
    const response = await fetch(`${API_BASE_URL}/api/users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(userData),
    });
    
    console.log('API Response Status:', response.status);
    const responseData = await response.json();
    console.log('API Response Data:', responseData);
    
    if (!response.ok) {
      throw new Error(responseData.error || 'Failed to create user');
    }
    
    return responseData;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
};

// Get roles for dropdown (fetches from roles table)
export const getRoles = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/roles`);
    if (!response.ok) throw new Error('Failed to fetch roles');
    return await response.json();
  } catch (error) {
    console.error('Error fetching roles:', error);
    return [];
  }
};

// Get working areas for dropdown (fetches from groups table)
export const getWorkingAreas = async (adminLocationId = null) => {
  try {
    let url = `${API_BASE_URL}/api/working-areas`;
    if (adminLocationId) {
      url += `?adminId=${adminLocationId}`;
    }
    
    const response = await fetch(url);
    if (!response.ok) throw new Error('Failed to fetch working areas');
    return await response.json();
  } catch (error) {
    console.error('Error fetching working areas:', error);
    return [];
  }
};

// Function to export data to Excel
export const exportToExcel = async (filters = {}) => {
  try {
    // Build query parameters from filters
    const queryParams = new URLSearchParams();
    
    if (filters.fromDate) queryParams.append('fromDate', filters.fromDate);
    if (filters.toDate) queryParams.append('toDate', filters.toDate);
    if (filters.locationId) queryParams.append('locationId', filters.locationId);
    if (filters.vehicleId) queryParams.append('vehicleId', filters.vehicleId);
    
    // Create a download link
    const link = document.createElement('a');
    link.href = `${API_BASE_URL}/api/user-wise-report/export?${queryParams.toString()}`;
    link.setAttribute('download', 'user-wise-report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    return true;
  } catch (error) {
    console.error('Error exporting data to Excel:', error);
    return false;
  }
};

// Login authentication
export const loginUser = async (employeeId, password) => {
  try {
    const response = await fetch(`${API_BASE_URL}/api/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ employeeId, password }),
    });
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Login failed');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

// Get users by role
export const getUsersByRole = async (roleId, locationId) => {
  try {
    console.log('Fetching users for roleId:', roleId, 'and locationId:', locationId);
    let url = `${API_BASE_URL}/api/users-by-role?roleId=${roleId}`;
    if (locationId) {
      url += `&locationId=${locationId}`;
    }
    const response = await fetch(url);
    console.log('Response status:', response.status);
    
    if (!response.ok) {
      console.error('Failed to fetch users. Status:', response.status);
      throw new Error('Failed to fetch users');
    }
    
    const data = await response.json();
    console.log('Received users data:', data);
    return data;
  } catch (error) {
    console.error('Error fetching users by role:', error);
    return [];
  }
}; 